import type { CityMap } from './map';

/** How well her phone's map app can route tonight. */
export type Signal = 'good' | 'weak' | 'none';

/** What the map app shows: the route as flattened [c, r, c, r, ...] cells, and a status line. */
export type RouteView = { cells: number[]; signal: Signal; note: string | null };

/** Weak signal: the route only refreshes this often (seconds). */
const WEAK_REFRESH = 6;
/** How long "Recalculating…" stays up after a barrier forces a new route. */
const RECALC_SHOW = 2.2;

/**
 * Turn-by-turn directions home, as the map app sees the streets. It finds the
 * quickest street route, including the shrine paths and the dark lanes, and
 * knows nothing about tonight's road works or flooding until she has seen the
 * barrier herself; then it recalculates. It never routes around him.
 */
export class Directions {
  /** Barrier cells she has seen (the app now avoids them). */
  private readonly known = new Set<number>();
  private cells: number[] = [];
  private lastCell = -1;
  private nextRefresh = 0;
  private recalcUntil = 0;
  private readonly home: { c: number; r: number } | null;
  private readonly parent: Int32Array;
  private readonly queue: Int32Array;

  constructor(
    private readonly map: CityMap,
    private readonly signal: Signal,
  ) {
    this.home = map.find('H')[0] ?? null;
    this.parent = new Int32Array(map.width * map.height);
    this.queue = new Int32Array(map.width * map.height);
  }

  update(time: number, c: number, r: number): void {
    // She can see a barrier from the cell next to it.
    let found = false;
    for (const [dc, dr] of [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nc = c + dc;
      const nr = r + dr;
      if (this.map.char(nc, nr) !== 'X') continue;
      const i = this.map.index(nc, nr);
      if (this.known.has(i)) continue;
      this.known.add(i);
      found = true;
    }
    if (this.signal === 'none') {
      this.cells = [];
      return;
    }
    const here = this.map.index(c, r);
    const due = this.signal === 'good' ? here !== this.lastCell || found : time >= this.nextRefresh || this.lastCell < 0;
    if (!due) return;
    this.lastCell = here;
    this.nextRefresh = time + WEAK_REFRESH;
    const before = this.cells;
    this.cells = this.route(c, r);
    // The old route ran through the barrier: show the app catching up.
    if (found && before.length > 0) this.recalcUntil = time + RECALC_SHOW;
  }

  view(time: number): RouteView {
    const note =
      this.signal === 'none'
        ? 'No signal'
        : time < this.recalcUntil
          ? 'Recalculating…'
          : this.signal === 'weak'
            ? 'Weak GPS signal'
            : null;
    return { cells: time < this.recalcUntil ? [] : this.cells, signal: this.signal, note };
  }

  /** Breadth-first search over the streets the app knows about. */
  private route(fromC: number, fromR: number): number[] {
    const map = this.map;
    const home = this.home;
    if (!home || !map.isStreet(fromC, fromR)) return [];
    const parent = this.parent.fill(-1);
    const queue = this.queue;
    const start = map.index(fromC, fromR);
    const goal = map.index(home.c, home.r);
    parent[start] = start;
    let head = 0;
    let tail = 0;
    queue[tail++] = start;
    while (head < tail) {
      const cur = queue[head++];
      if (cur === goal) break;
      const c = cur % map.width;
      const r = Math.floor(cur / map.width);
      for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nc = c + dc;
        const nr = r + dr;
        if (!map.inBounds(nc, nr) || !map.isStreet(nc, nr)) continue;
        const ni = map.index(nc, nr);
        if (parent[ni] !== -1 || this.known.has(ni)) continue;
        parent[ni] = cur;
        queue[tail++] = ni;
      }
    }
    if (parent[goal] === -1) return [];
    const out: number[] = [];
    for (let i = goal; ; i = parent[i]) {
      out.push(i % map.width, Math.floor(i / map.width));
      if (i === start) break;
    }
    // Pairs are reversed (goal first); flip to run from her to home.
    const cells: number[] = [];
    for (let k = out.length - 2; k >= 0; k -= 2) cells.push(out[k], out[k + 1]);
    return cells;
  }
}
